import {
  get,
  keyBy,
  filter,
  isEmpty,
  reject,
  chain,
  includes,
  map,
  sortBy,
  find,
  toLower,
  has,
  mapValues,
  concat,
  first,
  some,
} from "lodash";
import { isReference } from "@apollo/client/utilities";

import {
  parseRichTextContent,
  isRichTextContentEmpty,
} from "src/shared/RichTextContent/RichTextContent";
import { TAG_ATTRIBUTE_ACTION, TAG_OPERATIONS } from "src/consts/tags";
import {
  inNameAttributes,
  type InNameAttributeList,
  type TagTypeAttributesConfig,
} from "src/types/tagConfigAttributes";
import { isValidGqlFragment } from "./isValidGqlFragment";
import { readCacheField } from "./tagsEditorApolloCache";
import { TagMinimalFragment } from "./queries/fragments/tagFragment.graphql";
import { getTagsForEntity as getTagsForEntityQuery } from "./queries/tags.graphql";

export const MEMBER_TAG_REFETCH_QUERIES = ["getPeopleByIds", "getMembersById"];

export const tagTypeHasMandatoryEntityAttributes = (tagTypeConfig) =>
  some(tagTypeConfig?.attributes?.entityTag, {
    action: TAG_ATTRIBUTE_ACTION.MANDATORY,
  });

export const getCreatedEntityTagFromApplyResult = (result) =>
  result?.data?.result?.id ? result.data.result : null;

export const entityTagFromApplyResult = (selectedTag, createdEntityTag) => ({
  ...selectedTag,
  ...createdEntityTag,
  id: createdEntityTag.id,
  tagId: createdEntityTag.tagId,
  displayValue:
    createdEntityTag.tag?.displayValue ||
    createdEntityTag.displayValue ||
    selectedTag.displayValue,
  type: createdEntityTag.type || selectedTag.type,
  entityTagAttributes: createdEntityTag.entityTagAttributes || {},
  tag: createdEntityTag.tag,
});

export const isTagTypeConfigReadonly = (tagTypeConfig) => {
  return Boolean(tagTypeConfig?.isReadonly || tagTypeConfig?.readOnly);
};

// Create-new eligibility matches live admin "Allow users to add new tag values?"
// (`isEditableByUsers`). Do not gate on `allowNewTags` — that field has no admin
// UI and no working write path. Workspace configs may send `readOnly` instead of
// `isReadonly`; treat either as not creatable.
export const canCreateNewTagsForType = (tagTypeConfig) => {
  return (
    tagTypeConfig?.isEditableByUsers === true &&
    !isTagTypeConfigReadonly(tagTypeConfig)
  );
};

export type CreatableTagTypeConfig = {
  id?: string;
  isEditableByUsers?: boolean;
  isReadonly?: boolean;
  readOnly?: boolean;
};

export const getTagTypesEligibleForCreate = (
  tagTypesConfig: CreatableTagTypeConfig[] = []
) => {
  return filter(tagTypesConfig, canCreateNewTagsForType);
};

export const canCreateNewTags = (
  tagTypesConfig: CreatableTagTypeConfig[] = []
) => {
  return some(tagTypesConfig, canCreateNewTagsForType);
};

export const createTagSelectorOption = (tag) => {
  return {
    label: `${tag.tagTypeConfig.name}: ${tag.displayValue}`,
    value: tag.id,
    isFixed: tag.tagTypeConfig.isReadonly,
    data: {
      tag,
    },
  };
};

const parseRichTextForTooltips = (
  tagTypeConfigs: Array<Record<string, unknown>> = []
) => {
  return map(tagTypeConfigs, (tagTypeConfig) => {
    // todo: stop this function being called twice
    if (has(tagTypeConfig, "hasDetails")) {
      return tagTypeConfig;
    }
    const detailsParsed = parseRichTextContent(tagTypeConfig["details"]);
    const hasDetails = !isRichTextContentEmpty(detailsParsed);

    return { hasDetails, detailsParsed, ...tagTypeConfig };
  });
};

// ... to what :)
// todo: all this code that transforms between graphql backend and view model could be well tidier
export const transformTagTypeConfigs = (tagTypeConfigs) => {
  return parseRichTextForTooltips(tagTypeConfigs);
};

export const withValidTagTypeConfig = (entityTags, tagTypeConfigs) => {
  const tagTypeConfigById = keyBy(
    parseRichTextForTooltips(tagTypeConfigs),
    "id"
  );
  return chain(entityTags)
    .map((entityTag) => ({
      ...(entityTag.tag || {}),
      ...entityTag,
      tagTypeConfig: tagTypeConfigById[entityTag.type],
    }))
    .filter("tagTypeConfig")
    .value();
};

export const formatSelectedTagsForDisplay = (tags, tagTypeConfigs) => {
  const tagsWithConfig = withValidTagTypeConfig(tags, tagTypeConfigs);
  return sortBy(
    tagsWithConfig,
    ({ tagTypeConfig: { name: typeName, isReadonly }, displayValue }) => {
      return [!isReadonly, typeName, displayValue];
    }
  );
};

const withExistingTagCounts = (tagOptions, existingTags) => {
  const countsByTagTypeId = chain(existingTags)
    .reject((tag) => tag.status === TAG_OPERATIONS.REMOVED)
    .groupBy("type")
    .mapValues((tags) => tags.length)
    .value();
  return tagOptions.map((option) => {
    // TODO: avoid reaching into deeply nested structure
    // (to be fair, this whole module needs a rewrite)
    const {
      data: {
        tag: {
          tagTypeConfig: { id: tagTypeId },
        },
      },
    } = option;
    return { ...option, existingTagsOfType: countsByTagTypeId[tagTypeId] || 0 };
  });
};

export const createSelectableTagOptions = (
  availableTags,
  tagTypeConfigs,
  existingTags: Array<{ status?: string; tagId?: string; type?: string }> = []
) => {
  const tagsWithConfig = withValidTagTypeConfig(availableTags, tagTypeConfigs);
  const existingTagIds = map(
    filter(existingTags, (tag) => tag.status !== TAG_OPERATIONS.REMOVED),
    "tagId"
  );

  const selectableTags = reject(tagsWithConfig, ({ id, tagTypeConfig }) => {
    return tagTypeConfig.isReadonly || includes(existingTagIds, id);
  });

  const result = map(selectableTags, createTagSelectorOption);
  return withExistingTagCounts(result, existingTags);
};

export const canAddMultipleTagsForTagType = (tagType, tagTypeConfig) => {
  return get(
    find(tagTypeConfig, { id: tagType }),
    "allowMultipleOnEntity",
    false
  );
};

export const getUpdatingStateForAllTagsOfType = (
  tagsByType,
  tagType,
  state
) => {
  return mapValues(
    keyBy(
      map(get(tagsByType, `[${tagType}]`) || [], (t) => ({ id: t.id, state })),
      "id"
    ),
    "state"
  );
};

export const canAddMultiTags = (tagTypesConfig) => {
  if (tagTypesConfig.length === 1) {
    return tagTypesConfig[0].allowMultipleOnEntity;
  }
  return true;
};

export const modifyTags = (tags, tag, replace) => {
  const { type, tagId } = tag;

  const filterFunction = replace
    ? (t) => t.type !== type
    : (t) => t.tagId !== tagId;
  const otherTags = filter(tags, filterFunction);
  return [...otherTags, tag];
};

export const createTagTypesConfigLookup = (tagTypeConfig) => {
  const tagTypeConfigById = keyBy(tagTypeConfig, "id");
  return (tagTypeId) => {
    const config = get(tagTypeConfigById, tagTypeId, {});
    return {
      id: tagTypeId,
      name: tagTypeId,
      allowMultipleOnEntity: true,
      isReadonly: false,
      ...config,
    };
  };
};

export const getNewOptionData = (
  inputValue,
  tagTypesConfig,
  options: Array<{ data?: { tag?: { value?: string; type?: string } } }> = []
) => {
  const existingOption = find(options, {
    data: { tag: { value: toLower(inputValue) } },
  });
  const filteredTagTypesConfig = reject(tagTypesConfig, {
    id: existingOption?.data?.tag?.type,
  });
  if (!inputValue || isEmpty(filteredTagTypesConfig)) {
    return null;
  }
  return {
    label: inputValue,
    value: inputValue,
    options: filter(filteredTagTypesConfig, "isEditableByUsers").map(
      (tagTypeConfig) =>
        createTagSelectorOption({
          type: tagTypeConfig.id,
          displayValue: inputValue,
          isActive: true,
          value: inputValue,
          isUserCreatedTagValue: true,
          tagTypeConfig,
        })
    ),
  };
};

export const updateTagFragment = (
  proxy,
  result,
  fragment = TagMinimalFragment
) => {
  const { tagId, appliedCount } = result;

  if (!isValidGqlFragment(fragment)) {
    return;
  }

  const cached = proxy.readFragment({
    id: `Tag:${tagId}`,
    fragment,
    fragmentName: "TagMinimalFragment",
  });

  if (!cached) {
    return;
  }

  const tag = {
    ...cached,
    appliedCount,
  };

  proxy.writeFragment({
    id: `Tag:${tagId}`,
    fragment,
    data: tag,
  });
};

export const makeComments = (comments, tag) => {
  return comments && { comments: { ...comments, tag } };
};

export const makeBulkComment = (
  tagId,
  tagType,
  tagDisplayValue,
  entityId,
  entityName,
  entityType,
  metaData
) => {
  return {
    entity: {
      entityId,
      entityName,
      entityType,
    },
    tag: {
      tagId,
      tagType,
      displayValue: tagDisplayValue,
    },
    ...metaData,
  };
};

export const getTagTypesWithShowingHistory = (tagTypesConfig) => {
  return tagTypesConfig
    .filter(({ showPreviousAppliedTags }) => showPreviousAppliedTags)
    .map((tagType) => tagType.id);
};

export type TagCache = {
  readQuery: (opts: unknown) => { tags?: unknown[] } | null | undefined;
  writeQuery: (opts: unknown) => void;
};

export const addTagToRemovedList = ({
  tag,
  cache,
  tagTypesWithShowingHistory = [],
  entityId,
  query = getTagsForEntityQuery,
  entityType,
}: {
  tag: { type?: string; tagId?: string; status?: string };
  cache: TagCache;
  tagTypesWithShowingHistory?: string[];
  entityId: string;
  query?: unknown;
  entityType?: string;
}) => {
  if (tag?.type && tagTypesWithShowingHistory.includes(tag.type)) {
    const removedTag = { ...tag, status: "REMOVED" };
    const variables = {
      entityId,
      tagTypes: tagTypesWithShowingHistory,
      tagStatus: "REMOVED",
      entityType,
    };
    const removedTagsCache = cache.readQuery({
      query,
      variables,
    });
    if (removedTagsCache) {
      cache.writeQuery({
        query,
        variables,
        data: {
          tags: [...(removedTagsCache.tags ?? []), removedTag],
        },
      });
    }
  }
};

export const removeTagFromRemovedList = ({
  cache,
  entityId,
  tagTypesWithShowingHistory,
  tag,
  addedTags,
  query = getTagsForEntityQuery,
  replace,
  entityType,
}: {
  cache: TagCache;
  entityId: string;
  tagTypesWithShowingHistory?: string[];
  tag: { type?: string; tagId?: string };
  addedTags?: { tags?: Array<{ id?: string; type?: string; tagId?: string }> };
  query?: unknown;
  replace?: boolean;
  entityType?: string;
}) => {
  const variables = {
    entityId,
    tagTypes: tagTypesWithShowingHistory,
    tagStatus: "REMOVED",
    entityType,
  };
  const removedTagsCache = cache.readQuery({
    query,
    variables,
  });
  if (removedTagsCache) {
    const foundRemovedTag = find(
      removedTagsCache.tags,
      (t: { tagId?: string }) => t.tagId === tag.tagId
    );
    let tags = removedTagsCache.tags ?? [];
    if (foundRemovedTag) {
      tags = tags.filter((t: { tagId?: string }) => t.tagId !== tag.tagId);
    }
    if (replace && includes(tagTypesWithShowingHistory, tag.type)) {
      const prevTags = filter(addedTags?.tags, (t) => t.type === tag.type);
      const updatedTags = map(prevTags, (t) => ({
        ...t,
        status: "REMOVED",
      }));
      tags = concat(tags, updatedTags);
    }
    cache.writeQuery({
      query,
      variables,
      data: {
        tags,
      },
    });
  }
};

export const isTagDisplayValueEditable = (
  nameAttrTypes: InNameAttributeList,
  tagTypeConfig: { allowedToEditTagDisplayValue?: boolean } = {}
) => {
  // ponytail: only `values` compose the display name. `others` (legacy
  // editable included) must not lock the field — same as Manage Tags.
  return tagTypeConfig?.allowedToEditTagDisplayValue && isEmpty(nameAttrTypes);
};

export type TagTypeConfigEditShape = {
  allowedToEditTagDisplayValue?: boolean;
  isReadonly?: boolean;
  readOnly?: boolean;
  attributes?: TagTypeAttributesConfig;
};

export const hasEditableUniqueAttributes = (
  tagTypeConfig: TagTypeConfigEditShape = {}
) => {
  if (isTagTypeConfigReadonly(tagTypeConfig)) {
    return false;
  }

  return some(
    tagTypeConfig?.attributes?.entityTag,
    (attribute) => attribute.action !== TAG_ATTRIBUTE_ACTION.READONLY
  );
};

export const hasEditableTagLevelAttributes = (
  tagTypeConfig: TagTypeConfigEditShape = {}
) => {
  if (isTagTypeConfigReadonly(tagTypeConfig)) {
    return false;
  }

  const meta = tagTypeConfig?.attributes || {};
  const tagLevelAttrs = [...(meta.values || []), ...(meta.others || [])];

  return some(
    tagLevelAttrs,
    (attribute) => attribute.action !== TAG_ATTRIBUTE_ACTION.READONLY
  );
};

/** Tag chip pencil in TagsEditor / TagSelector (value context). */
export const shouldShowTagChipEditButton = (
  tagTypeConfig: TagTypeConfigEditShape = {}
) => {
  if (isTagTypeConfigReadonly(tagTypeConfig)) {
    return false;
  }

  const nameAttrTypes = inNameAttributes(tagTypeConfig.attributes);

  if (isTagDisplayValueEditable(nameAttrTypes, tagTypeConfig)) {
    return true;
  }

  if (hasEditableUniqueAttributes(tagTypeConfig)) {
    return true;
  }

  return hasEditableTagLevelAttributes(tagTypeConfig);
};

export const canNavigate = ({ tagTypesConfig, tag }) => {
  const tagConfig = find(tagTypesConfig, (tagType) => tagType.id === tag.type);
  return includes(tagConfig?.visibleIn, "Navigation");
};

export const mergeEntityTagCacheRecord = (existing, updated) => {
  if (!updated) {
    return existing;
  }
  if (!existing) {
    return updated;
  }

  return {
    ...existing,
    ...updated,
    entityTagAttributes:
      updated.entityTagAttributes !== undefined &&
      updated.entityTagAttributes !== null
        ? updated.entityTagAttributes
        : existing.entityTagAttributes,
    tag: updated.tag
      ? { ...(existing.tag || {}), ...updated.tag }
      : existing.tag,
  };
};

export const updateTagsForEntityCache =
  ({
    entityId,
    tagTypes,
    entityTag,
    entityType,
  }: {
    entityId: string;
    tagTypes: string[];
    entityTag: { id?: string };
    entityType?: string;
  }) =>
  (cache, { data: { tag: updatedTag } }) => {
    const variables = {
      entityId,
      tagTypes,
      ...(entityType && { entityType }),
    };

    let cached;
    try {
      cached = cache.readQuery({
        query: getTagsForEntityQuery,
        variables,
      });
    } catch {
      return;
    }

    const existingCachedTags = cached?.tags || [];

    const tags = map(existingCachedTags, (cachedTag) => {
      const cachedEntityTagId = readCacheField(cache, "id", cachedTag);
      if (cachedEntityTagId !== entityTag?.id) {
        return cachedTag;
      }
      if (isReference(cachedTag)) {
        return cachedTag;
      }
      return mergeEntityTagCacheRecord(cachedTag, updatedTag);
    });

    cache.writeQuery({
      query: getTagsForEntityQuery,
      variables,
      data: {
        tags,
      },
    });
  };

export const filterOptions = (existingTags, ...args) => {
  const tagData = first(args);
  const tagExists = find(existingTags, (existingTag) => {
    return (
      existingTag?.status !== TAG_OPERATIONS.REMOVED &&
      (tagData?.value === existingTag.tagId ||
        tagData?.value === existingTag?.tag?.id)
    );
  });
  if (tagExists) {
    return false;
  }
  return true;
};

/**
 * Transforms EntityTag[] structure from getTagsForEntity query
 * to the format expected by Person/Team fragments (appliedTags)
 * @param {Array} entityTags - Array of EntityTag objects with nested tag data
 * @returns {Array} Transformed tags matching Person fragment structure
 */
export const transformEntityTagsForCache = (entityTags) => {
  return map(entityTags, (tag) => ({
    id: tag.id,
    tagId: tag.tagId,
    type: tag.type,
    displayValue: tag.tag?.displayValue || tag.displayValue,
    isActive: tag.tag?.isActive ?? tag.isActive,
    attributes: tag.tag?.attributes || tag.attributes,
    entityTagAttributes: tag.entityTagAttributes,
    __typename: "EntityTag",
  }));
};
